import { CheckIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useEffect, useRef, useState } from "react"
import type {
  PartnershipContentRowDto,
  PartnershipRightsStatus,
  PartnershipWindowAnchor,
  UpdatePartnershipContentRequest,
  UpdatePartnershipRequest,
} from "~/ui-rtk/api/types/partnerships.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  RIGHTS_STATUS_OPTIONS,
  WINDOW_ANCHOR_LABEL,
  WINDOW_ANCHOR_OPTIONS,
} from "./partnership-copy"

/**
 * Inline editors for one PartnershipContent row. Each editor gets a save
 * callback that resolves to `true` on success (the tab owns the mutation +
 * toast) so the editor knows whether to close.
 */

export type SavePartnershipFn = (
  partnershipId: string,
  body: UpdatePartnershipRequest,
) => Promise<boolean>

export type SaveContentFn = (
  contentId: string,
  body: UpdatePartnershipContentRequest,
) => Promise<boolean>

export const NO_PARTNERSHIP_TOOLTIP =
  "Link this post to a creator first (Add creator, then edit the row)"

/* ----- Rights status ----- */

interface RightsStatusSelectProps {
  row: PartnershipContentRowDto
  onSavePartnership: SavePartnershipFn
}
export function RightsStatusSelect({
  row,
  onSavePartnership,
}: RightsStatusSelectProps) {
  const [saving, setSaving] = useState(false)
  const partnership = row.partnership
  const handleChange = async (next: PartnershipRightsStatus) => {
    if (!partnership || next === partnership.rightsStatus) return
    setSaving(true)
    await onSavePartnership(partnership.id, { rightsStatus: next })
    setSaving(false)
  }
  return (
    <span title={partnership ? undefined : NO_PARTNERSHIP_TOOLTIP}>
      <Select
        aria-label={`Rights status for ${row.partnership?.creatorHandle ?? "this post"}`}
        options={RIGHTS_STATUS_OPTIONS}
        value={partnership?.rightsStatus ?? "META_PERMISSION_ONLY"}
        disabled={!partnership || saving}
        onChange={e =>
          void handleChange(e.target.value as PartnershipRightsStatus)
        }
        className="h-9 w-48 text-sm"
      />
    </span>
  )
}

/* ----- Usage window ----- */

/** Days-left copy + tone: red once the window is past, amber inside a week. */
export function remainingDaysLabel(remaining: number): {
  label: string
  cls: string
} {
  if (remaining < 0) {
    return {
      label: `${Math.abs(remaining)} day${remaining === -1 ? "" : "s"} over`,
      cls: "text-system-red-500",
    }
  }
  return {
    label: `${remaining} day${remaining === 1 ? "" : "s"} left`,
    cls: remaining <= 7 ? "text-system-yellow-500" : "text-system-gray-300",
  }
}

function hasContentOverride(row: PartnershipContentRowDto): boolean {
  return (
    row.usageDaysOverride != null ||
    row.startDateOverride != null ||
    row.endDateOverride != null
  )
}

interface WindowEditorDialogProps {
  row: PartnershipContentRowDto
  onClose: () => void
  onSavePartnership: SavePartnershipFn
  onSaveContent: SaveContentFn
}
/**
 * Small modal editor for the usage window. Two save targets: the creator's
 * deal (usageDays / anchor / dates on the Partnership) or a per-content
 * override (the *Override fields on this row). Save on button or Enter,
 * Escape / backdrop cancels — same contract as InlineEditCell.
 */
function WindowEditorDialog({
  row,
  onClose,
  onSavePartnership,
  onSaveContent,
}: WindowEditorDialogProps) {
  const partnership = row.partnership
  const initialOverride = !partnership || hasContentOverride(row)
  const [override, setOverride] = useState(initialOverride)
  const [usageDays, setUsageDays] = useState(() => {
    const v = initialOverride ? row.usageDaysOverride : partnership?.usageDays
    return v == null ? "" : String(v)
  })
  const [anchor, setAnchor] = useState<PartnershipWindowAnchor>(
    partnership?.windowAnchor ?? row.derived.windowAnchor,
  )
  const [startDate, setStartDate] = useState(
    (initialOverride ? row.startDateOverride : partnership?.startDate) ?? "",
  )
  const [endDate, setEndDate] = useState(
    (initialOverride ? row.endDateOverride : partnership?.endDate) ?? "",
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    const trimmed = usageDays.trim()
    let days: number | null = null
    if (trimmed) {
      days = Number(trimmed)
      if (!Number.isInteger(days) || days < 1 || days > 3650) {
        setError("Usage days must be a whole number between 1 and 3650")
        return
      }
    }
    if (startDate && endDate && endDate < startDate) {
      setError("End date must be on or after the start date")
      return
    }
    setError(null)
    setSaving(true)
    const ok = override
      ? await onSaveContent(row.id, {
          usageDaysOverride: days,
          startDateOverride: startDate || null,
          endDateOverride: endDate || null,
        })
      : await onSavePartnership(partnership?.id ?? "", {
          usageDays: days,
          windowAnchor: anchor,
          startDate: startDate || null,
          endDate: endDate || null,
        })
    setSaving(false)
    if (ok) onClose()
  }

  return (
    <Dialog
      isOpen
      handleClose={onClose}
      closeOnClickOutside
      width={380}
      classes={{
        container:
          "rounded-lg border border-basic-blue bg-basic-dark-blue p-4 text-left text-white shadow-xl",
      }}
    >
      <form
        onSubmit={e => {
          e.preventDefault()
          void handleSave()
        }}
        onKeyDown={e => {
          if (e.key === "Escape") onClose()
        }}
        className="flex flex-col gap-3"
      >
        <h3 className="text-base font-semibold">Usage window</h3>
        <label className="flex flex-col gap-1 text-xs text-gray-300">
          Usage days
          <Input
            fullWidth
            type="number"
            min={1}
            max={3650}
            aria-label="Usage days"
            placeholder="e.g. 90"
            value={usageDays}
            onChange={e => setUsageDays(e.target.value)}
            autoFocus
          />
        </label>
        {!override && (
          <label className="flex flex-col gap-1 text-xs text-gray-300">
            Window starts
            <Select
              aria-label="Window anchor"
              options={WINDOW_ANCHOR_OPTIONS}
              value={anchor}
              onChange={e =>
                setAnchor(e.target.value as PartnershipWindowAnchor)
              }
              className="h-9 text-sm"
            />
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs text-gray-300">
            Start date
            <Input
              fullWidth
              type="date"
              aria-label="Start date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-300">
            End date
            <Input
              fullWidth
              type="date"
              aria-label="End date"
              value={endDate}
              min={startDate || undefined}
              onChange={e => setEndDate(e.target.value)}
            />
          </label>
        </div>
        <span title={partnership ? undefined : NO_PARTNERSHIP_TOOLTIP}>
          <CheckboxWithLabel
            checked={override}
            disabled={!partnership}
            onChange={e => setOverride(e.target.checked)}
          >
            <span className="text-xs text-gray-300">
              Override for this post only (leave the creator&apos;s deal
              unchanged)
            </span>
          </CheckboxWithLabel>
        </span>
        {error && <p className="text-xs text-states-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary btn-sm"
          >
            {saving && <span className="loading loading-spinner loading-xs" />}
            {override ? "Save override" : "Save for creator"}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

interface WindowCellProps {
  row: PartnershipContentRowDto
  onSavePartnership: SavePartnershipFn
  onSaveContent: SaveContentFn
}
export function WindowCell({
  row,
  onSavePartnership,
  onSaveContent,
}: WindowCellProps) {
  const [editing, setEditing] = useState(false)
  const d = row.derived
  const remaining =
    d.remainingDays == null ? null : remainingDaysLabel(d.remainingDays)
  return (
    <div className="group flex min-w-0 flex-col gap-0.5">
      <span className="text-xs text-gray-400">
        {WINDOW_ANCHOR_LABEL[d.windowAnchor]}
        {hasContentOverride(row) && " · override"}
      </span>
      <div className="flex items-center gap-1">
        <span className="whitespace-nowrap text-sm text-white">
          {d.windowStart ?? "?"} → {d.effectiveEnd ?? "?"}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit usage window"
          title="Edit usage window"
          className="rounded p-1 opacity-0 transition-opacity hover:bg-system-blue-800 group-hover:opacity-100 focus:opacity-100"
        >
          <PencilIcon className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>
      {remaining && (
        <span className={cn("text-xs font-medium", remaining.cls)}>
          {remaining.label}
        </span>
      )}
      {editing && (
        <WindowEditorDialog
          row={row}
          onClose={() => setEditing(false)}
          onSavePartnership={onSavePartnership}
          onSaveContent={onSaveContent}
        />
      )}
    </div>
  )
}

/* ----- Ad code ----- */

interface AdCodeCellProps {
  row: PartnershipContentRowDto
  onSaveContent: SaveContentFn
}
/**
 * The creator-supplied partnership ad code. The API only ever returns a
 * 4-char hint, so the editor is write-only: a password input that sets a new
 * code, or "Clear" which sends `null`.
 */
export function AdCodeCell({ row, onSaveContent }: AdCodeCellProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState("")
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const close = () => {
    setEditing(false)
    setValue("")
  }

  const save = async (code: string | null) => {
    if (code !== null && !code.trim()) return
    setSaving(true)
    const ok = await onSaveContent(row.id, {
      adCode: code === null ? null : code.trim(),
    })
    setSaving(false)
    if (ok) close()
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="password"
            aria-label="Partnership ad code"
            placeholder="Paste ad code"
            value={value}
            autoComplete="off"
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault()
                void save(value)
              }
              if (e.key === "Escape") close()
            }}
            className="w-28 rounded border border-basic-blue bg-basic-black px-2 py-1 text-sm text-white"
          />
          <button
            type="button"
            onClick={() => void save(value)}
            disabled={saving || !value.trim()}
            aria-label="Save ad code"
            className="rounded p-1 hover:bg-system-blue-800 disabled:opacity-50"
          >
            <CheckIcon className="h-4 w-4 text-american-green" />
          </button>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            aria-label="Cancel ad code edit"
            className="rounded p-1 hover:bg-system-blue-800"
          >
            <XMarkIcon className="h-4 w-4 text-states-error" />
          </button>
        </div>
        {row.hasAdCode && (
          <button
            type="button"
            onClick={() => void save(null)}
            disabled={saving}
            className="self-start text-xs font-medium text-system-red-500 hover:underline disabled:opacity-60"
          >
            Clear
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="group flex items-center gap-1">
      <span
        className={cn(
          "font-mono text-sm",
          row.hasAdCode ? "text-white" : "text-gray-400",
        )}
        title={
          row.hasAdCode && row.adCodeSetAt
            ? `Set ${row.adCodeSetAt.slice(0, 10)}`
            : undefined
        }
      >
        {row.hasAdCode ? `•••• ${row.adCodeHint ?? ""}`.trim() : "—"}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Edit ad code"
        title={
          row.hasAdCode ? "Replace or clear the ad code" : "Add an ad code"
        }
        className="rounded p-1 opacity-0 transition-opacity hover:bg-system-blue-800 group-hover:opacity-100 focus:opacity-100"
      >
        <PencilIcon className="h-3.5 w-3.5 text-gray-400" />
      </button>
    </div>
  )
}

/* ----- Notes ----- */

interface NotesCellProps {
  row: PartnershipContentRowDto
  onSaveContent: SaveContentFn
}
export function NotesCell({ row, onSaveContent }: NotesCellProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(row.notes ?? "")
  const [saving, setSaving] = useState(false)

  const open = () => {
    setValue(row.notes ?? "")
    setEditing(true)
  }
  const save = async () => {
    setSaving(true)
    const ok = await onSaveContent(row.id, { notes: value.trim() || null })
    setSaving(false)
    if (ok) setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <textarea
          value={value}
          rows={2}
          autoFocus
          aria-label="Notes"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Escape") setEditing(false)
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save()
          }}
          className="textarea textarea-bordered textarea-xs w-56"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="text-xs font-medium text-brand hover:underline disabled:opacity-60"
          >
            Save note
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="text-xs text-gray-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={open}
      title={row.notes ?? "Add a note"}
      className="max-w-56 truncate text-left text-xs italic text-gray-400 hover:text-white"
    >
      {row.notes ? row.notes : "Add note"}
    </button>
  )
}
